// O GANCHO DO CADERNO VAZIO (31/10/2026, auditoria do percurso da conta nova,
// seção 3, ideia 1). Um caderno de trilha em branco avisava só que o espaço
// existia. Agora, se a Travessia já abriu um insight com a pergunta "Para o
// caderno", o caderno vazio mostra essa pergunta — a do insight liberado mais
// recente — e o botão leva à escrita da nota pelo mesmo caminho do "Responder
// no caderno" da folha do insight (o compositor aberto na página do insight).
// Nenhum texto novo: o título e a pergunta são os do Mentor. Sem insight com
// pergunta, quem chama mantém o aviso de sempre.
import React, { useEffect, useState } from 'react';
import { api } from '../../services/apiV2';
import type { TrilhaItem } from '../../services/apiV2';
import type { AuthSession } from '../../types';
import type { ContextoDaNota } from '../nota-composer';

/** A pergunta do caderno sai do corpo do insight ("**Para o caderno.** …"). */
export function separarPergunta(corpo: string): { resto: string; pergunta: string | null } {
  const m = corpo.match(/\n?\s*\*\*Para o caderno\.?\*\*\s*([\s\S]+)$/i);
  if (!m) return { resto: corpo, pergunta: null };
  return { resto: corpo.slice(0, m.index).trimEnd(), pergunta: m[1].trim() };
}

/** A pergunta do insight como contexto do editor de nota (a folha do insight e o caderno vazio usam o mesmo). */
export function contextoDoInsight(titulo: string | null | undefined, pagina: number, pergunta: string): ContextoDaNota {
  return { pergunta: pergunta.replace(/\*\*|__/g, ''), origem: [titulo, `p. ${pagina}`].filter(Boolean).join(' · ') };
}

export type Gancho = { itemId: string; titulo: string; pergunta: string; pagina: number };

/** O insight liberado mais recente que traz pergunta para o caderno (empate: a página maior). */
export function ganchoDosItens(itens: TrilhaItem[]): Gancho | null {
  const comPergunta = itens
    .filter((i) => i.forma === 'insight' && i.titulo)
    .map((i) => ({ i, pergunta: separarPergunta(i.corpo || '').pergunta }))
    .filter((x): x is { i: TrilhaItem; pergunta: string } => !!x.pergunta)
    .sort((a, b) => String(b.i.liberado_em).localeCompare(String(a.i.liberado_em)) || b.i.pagina_gatilho - a.i.pagina_gatilho);
  const x = comPergunta[0];
  return x ? { itemId: x.i.id, titulo: x.i.titulo as string, pergunta: x.pergunta.replace(/\*\*|__/g, ''), pagina: x.i.pagina_gatilho } : null;
}

/** Busca o gancho de um livro com Travessia; `ativo` falso não busca (caderno com notas, livro sem trilha). */
export function useGanchoDoCaderno(session: AuthSession | null, bookId: string, ativo: boolean): Gancho | null {
  const [gancho, setGancho] = useState<Gancho | null>(null);
  useEffect(() => {
    if (!ativo || !session) { setGancho(null); return undefined; }
    let vivo = true;
    api.travessia(session, bookId)
      .then((m) => { if (vivo) setGancho(ganchoDosItens(m.itens || [])); })
      .catch(() => { if (vivo) setGancho(null); });
    return () => { vivo = false; };
  }, [session, bookId, ativo]);
  return gancho;
}

export const CartaoGancho: React.FC<{ gancho: Gancho; onResponder: (pagina: number, contexto: ContextoDaNota) => void; style?: React.CSSProperties }> = ({ gancho, onResponder, style }) => (
  <div data-gancho-caderno={gancho.itemId} style={{ padding: '14px 14px 16px', borderRadius: 16, background: 'var(--paper-2)', border: '1px solid var(--line-strong)', textAlign: 'left', ...style }}>
    <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ember)' }}>Para o caderno</div>
    <div data-gancho-titulo style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 6 }}>{gancho.titulo} · p. {gancho.pagina}</div>
    <p data-gancho-pergunta className="serif" style={{ margin: '8px 0 14px', fontSize: 20, lineHeight: 1.3, color: 'var(--ink)' }}>{gancho.pergunta}</p>
    <button type="button" onClick={() => onResponder(gancho.pagina, contextoDoInsight(gancho.titulo, gancho.pagina, gancho.pergunta))} data-gancho-responder
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 44, padding: '0 18px', borderRadius: 14, border: 'none', background: 'var(--ember)', color: '#1c1208', fontFamily: 'inherit', fontSize: 14.5, fontWeight: 600, cursor: 'pointer' }}>
      Responder no caderno
    </button>
  </div>
);
